/**
 * Helpers shared by the leave approval screens (inbox, HR final queue, rules,
 * employee side).
 *
 * Time handling: every `*At` stamp on the approval views is a UTC instant with a
 * trailing `Z` that the server does NOT shift, so `formatInstant` lets dayjs
 * convert it to the viewer's clock. Leave dates (start, end, applied) are
 * calendar dates; `formatCalendarDate` reads the date part only, so a midnight
 * instant never slips to the day before.
 */

import dayjs from 'dayjs';
import type {
  LeaveApprovalAskView,
  LeaveApprovalPersonView,
  LeaveModelView,
} from '@actschurch/ccms-sdk';

/** Prefilled message for "Ask for duty cover" (editable before sending). */
export const DUTY_COVER_PREFILL = "Please arrange who will cover your duties while you're away.";

/** UTC instant to the viewer's local "DD MMM YYYY HH:mm". Empty string when missing. */
export function formatInstant(iso?: string | null): string {
  if (!iso) return '';
  const d = dayjs(iso);
  return d.isValid() ? d.format('DD MMM YYYY HH:mm') : '';
}

/** Calendar date (date only, or a midnight instant) to "DD MMM YYYY" without a time zone shift. */
export function formatCalendarDate(input?: string | null): string {
  if (!input) return '';
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(input);
  const d = match ? dayjs(match[1]) : dayjs(input);
  return d.isValid() ? d.format('DD MMM YYYY') : '';
}

/** "12 Oct 2026 to 16 Oct 2026", or one date when start and end match. */
export function formatDateRange(start?: string | null, end?: string | null): string {
  const a = formatCalendarDate(start);
  const b = formatCalendarDate(end);
  if (!a) return b;
  if (!b || a === b) return a;
  return `${a} to ${b}`;
}

export function formatDayCount(n?: number | null): string {
  if (n == null) return '';
  return n === 1 ? '1 day' : `${n} days`;
}

/** Display name for a person on an approval view. */
export function personName(p?: LeaveApprovalPersonView | null, fallback = 'Someone'): string {
  if (!p) return fallback;
  return (
    p.fullName?.trim() ||
    [p.firstName, p.surname].filter(Boolean).join(' ').trim() ||
    p.email?.trim() ||
    fallback
  );
}

/** "Jane", "Jane and Sam", "Jane, Sam and Lee" (no Oxford comma). */
export function joinNames(names: (string | null | undefined)[]): string {
  const clean = names.map((n) => (n ?? '').trim()).filter((n) => n.length > 0);
  if (clean.length <= 1) return clean[0] ?? '';
  return `${clean.slice(0, -1).join(', ')} and ${clean[clean.length - 1]}`;
}

export function joinPeople(people?: LeaveApprovalPersonView[] | null): string {
  return joinNames((people ?? []).map((p) => personName(p, '')));
}

/** The applicant's full name from a LeaveModelView. */
export function applicantName(leave?: LeaveModelView | null, fallback = 'The employee'): string {
  if (!leave) return fallback;
  return (
    leave.fullName?.trim() ||
    [leave.name, leave.surname].filter(Boolean).join(' ').trim() ||
    leave.email?.trim() ||
    fallback
  );
}

/** The applicant's first name, for friendly copy ("Sent back to Jane"). */
export function applicantFirstName(leave?: LeaveModelView | null, fallback = 'the employee'): string {
  if (!leave) return fallback;
  return leave.name?.trim() || applicantName(leave, fallback).split(' ')[0] || fallback;
}

export function initialsOf(name?: string | null): string {
  if (!name) return '?';
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? '')
      .join('') || '?'
  );
}

/** Plain text from the HTML the leave forms store (reason, comment). */
export function plainText(html?: string | null): string {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&quot;/gi, '"')
    .trim();
}

/** True when the ask is still waiting for the employee. */
export function isAskOpen(ask?: LeaveApprovalAskView | null): ask is LeaveApprovalAskView {
  return !!ask && ask.isOpen !== false;
}

/** Mantine colour for a chain stage name (the server's ready-made label). */
export function stageColor(stageName?: string | null): string {
  const s = (stageName ?? '').toLowerCase();
  if (s.startsWith('approved')) return 'green';
  if (s.startsWith('rejected')) return 'red';
  if (s.startsWith('cancelled')) return 'gray';
  if (s.startsWith('deferred')) return 'blue';
  if (s.includes('employee')) return 'orange';
  if (s.includes('hr final')) return 'violet';
  if (s.includes('finding')) return 'gray';
  return 'yellow';
}

interface ErrorShape {
  message?: string;
  status?: number;
  errors?: string[];
}

/**
 * The server's message for a refused action. The approval API sends a ready-to-show
 * SA English sentence with every 400/403/404/409, so it is shown as is; generic
 * transport messages fall back to `fallback`.
 */
export function approvalErrorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (!err) return fallback;
  const e = err as ErrorShape;
  if (e.status === 401) return 'Your session has expired. Sign in again.';
  const message = typeof e.message === 'string' ? e.message.trim() : '';
  const generic =
    !message ||
    /^Request failed with status/i.test(message) ||
    /^HTTP \d+/i.test(message) ||
    /^Failed to fetch$/i.test(message) ||
    /Response does not conform/i.test(message);
  if (!generic) return message;
  const extra = (e.errors ?? []).filter((x) => typeof x === 'string' && x.trim().length > 0);
  if (extra.length) return extra.join(' ');
  if (e.status === 403) return "You don't have permission to do this.";
  if (e.status === 404) return "This leave isn't available to you.";
  if (e.status === 408 || /timeout/i.test(message)) return 'The server took too long to answer. Please try again.';
  return fallback;
}

/** HTTP status of an SDK error, if any. */
export function errorStatus(err: unknown): number | undefined {
  return (err as ErrorShape | null)?.status;
}
